/*
 * Horgászkellék — Szettválasztó (sections/hk-szettvalaszto.liquid).
 * Hal + méret kiválasztása után bot, orsó, zsinór (egy-egy) és csali (több is)
 * a szekcióba ágyazott JSON-ból; a „Szett a kosárba” egyetlen /cart/add.js
 * hívással teszi kosárba a kiválasztott változatokat, majd a kosárra ugrik.
 * A választás a címsorba is bekerül (?hal=ponty&meret=8-15), így megosztható.
 */
if (!customElements.get('hk-szettvalaszto')) {
  const SINGLE = ['rod', 'reel', 'line'];
  // Csaliból egyszerre ennyi látszik; a „Több csali” gomb újabb ennyit mutat
  const BAIT_PAGE = 4;
  // A bolt árformátuma („6 990 Ft”) — az Intl hu-HU a négyjegyűt nem tagolná
  const formatPrice = (amount) => `${String(Math.round(amount)).replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0')}\u00a0Ft`;

  class HkSzettvalaszto extends HTMLElement {
    connectedCallback() {
      try {
        this.data = JSON.parse(this.querySelector('[data-hk-szett-data]').textContent);
      } catch (error) {
        console.error('Szettválasztó: hibás adat', error);
        return;
      }
      this.uid = this.closest('[id^="shopify-section"]')?.id ?? 'hk-szett';
      this.sizeStep = this.querySelector('[data-step="size"]');
      this.sizeHint = this.querySelector('[data-size-hint]');
      this.gear = this.querySelector('[data-gear]');
      this.summary = this.querySelector('[data-summary]');
      this.addButton = this.querySelector('[data-add]');
      this.error = this.querySelector('[data-error]');

      this.querySelector('[data-step="fish"]').addEventListener('change', (event) => this.onFish(event.target.value, true));
      this.sizeStep.addEventListener('change', (event) => this.onSize(event.target.value, true));
      this.gear.addEventListener('change', (event) => this.onGearChange(event));
      this.addButton.addEventListener('click', () => this.addToCart());

      // Megosztott link: ?hal=…&meret=…
      const params = new URLSearchParams(window.location.search);
      const fishInput = params.get('hal') && this.querySelector(`[data-step="fish"] input[value="${CSS.escape(params.get('hal'))}"]`);
      if (fishInput) {
        fishInput.checked = true;
        this.onFish(fishInput.value, false);
        const sizeInput = params.get('meret') && this.sizeStep.querySelector(`input[value="${CSS.escape(params.get('meret'))}"]`);
        if (sizeInput && !sizeInput.disabled) {
          sizeInput.checked = true;
          this.onSize(sizeInput.value, false);
        }
      }
    }

    get fish() {
      return this.querySelector('[data-step="fish"] input:checked')?.value ?? null;
    }

    get size() {
      return this.sizeStep.querySelector('input:checked:not(:disabled)')?.value ?? null;
    }

    onFish(fishId, byUser) {
      const fish = this.data.fish.find((f) => f.id === fishId);
      if (!fish) return;
      this.sizeStep.disabled = false;
      this.sizeHint.hidden = true;
      const inputs = [...this.sizeStep.querySelectorAll('input')];
      for (const input of inputs) {
        const allowed = fish.sizes.includes(input.value);
        input.disabled = !allowed;
        if (!allowed) input.checked = false;
        input.closest('.hk-szett__choice').classList.toggle('is-unavailable', !allowed);
      }
      // Ha csak egy méret értelmes (keszeg), azt választjuk ki helyette
      if (fish.sizes.length === 1) {
        inputs.find((i) => i.value === fish.sizes[0]).checked = true;
      }
      if (this.size) this.onSize(this.size, byUser);
      else {
        this.gear.hidden = true;
        this.summary.hidden = true;
        this.updateUrl();
      }
    }

    onSize(sizeId, byUser) {
      const set = this.data.sets[`${this.fish}|${sizeId}`];
      if (!set) return;
      for (const slot of this.gear.querySelectorAll('[data-slot]')) {
        this.renderSlot(slot, set[slot.dataset.slot] ?? [], set.tiers?.[slot.dataset.slot]);
      }
      this.gear.hidden = false;
      this.summary.hidden = false;
      this.updateSummary();
      this.updateUrl();
      if (byUser) {
        const heading = this.querySelector('[data-gear-heading]');
        heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
        heading.focus({ preventScroll: true });
      }
    }

    // Egy hely (bot, orsó, zsinór, csali) opciói; az első opció előre ki van választva
    // tiers: opciónként az ársáv indexe (Belépő … Prémium), ha a hely négy sávra bontható
    renderSlot(slotEl, options, tiers) {
      const slot = slotEl.dataset.slot;
      const container = slotEl.querySelector('[data-options]');
      const multi = !SINGLE.includes(slot);
      const name = `${this.uid}-${slot}`;
      container.replaceChildren();
      slotEl.querySelector('[data-more]')?.remove();

      if (!options.length) {
        const note = document.createElement('p');
        note.className = 'hk-szett__hint hk-szett__none';
        note.textContent = 'Ehhez a mérethez most nincs raktáron megfelelő termék.';
        container.append(note);
        return;
      }

      options.forEach((variantIds, index) => {
        const first = this.data.variants[variantIds[0]];
        const product = this.data.products[first.p];
        const option = document.createElement('div');
        option.className = 'hk-szett__option';

        const label = document.createElement('label');
        label.className = 'hk-szett__option-main';
        const input = document.createElement('input');
        input.className = 'hk-szett__input';
        input.type = multi ? 'checkbox' : 'radio';
        input.name = name;
        input.value = variantIds[0];
        input.checked = index === 0;
        const media = document.createElement('span');
        media.className = 'hk-szett__media';
        if (product.i) {
          const img = document.createElement('img');
          img.src = product.i;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 400;
          img.height = 400;
          media.append(img);
        }
        const title = document.createElement('span');
        title.className = 'hk-szett__option-title';
        title.textContent = product.t;
        label.append(input, media);
        if (tiers && this.data.tiers?.[tiers[index]]) {
          const tier = document.createElement('span');
          tier.className = 'hk-szett__tier';
          tier.textContent = this.data.tiers[tiers[index]];
          label.append(tier);
        }
        label.append(title);

        const foot = document.createElement('div');
        foot.className = 'hk-szett__option-foot';
        if (variantIds.length > 1) {
          const select = document.createElement('select');
          select.className = 'hk-szett__variant';
          select.setAttribute('aria-label', `${product.t} változata`);
          for (const id of variantIds) {
            const v = this.data.variants[id];
            select.append(new Option(v.l || 'Alap', id));
          }
          foot.append(select);
        } else if (first.l) {
          const variantText = document.createElement('span');
          variantText.className = 'hk-szett__variant-text';
          variantText.textContent = first.l;
          foot.append(variantText);
        }
        const price = document.createElement('span');
        price.className = 'hk-szett__price';
        price.textContent = formatPrice(first.pr);
        const details = document.createElement('a');
        details.className = 'hk-szett__details link';
        details.href = `${product.u}?variant=${variantIds[0]}`;
        details.target = '_blank';
        details.rel = 'noopener';
        details.textContent = 'Részletek';
        foot.append(price, details);

        option.append(label, foot);
        option.classList.toggle('is-selected', input.checked);
        if (multi && index >= BAIT_PAGE) option.hidden = true;
        container.append(option);
      });

      // Csali: az első négy látszik, a gomb négyesével mutatja a többit
      if (multi && options.length > BAIT_PAGE) {
        const more = document.createElement('button');
        more.type = 'button';
        more.className = 'button button--secondary hk-szett__more';
        more.dataset.more = '';
        const refresh = () => {
          const left = container.querySelectorAll('.hk-szett__option[hidden]').length;
          more.textContent = `Több csali (${left})`;
          more.hidden = left === 0;
        };
        more.addEventListener('click', () => {
          const next = [...container.querySelectorAll('.hk-szett__option[hidden]')].slice(0, BAIT_PAGE);
          next.forEach((el) => { el.hidden = false; });
          refresh();
          next[0]?.querySelector('input')?.focus({ preventScroll: true });
        });
        refresh();
        container.after(more);
      }

      if (!multi) {
        const skip = document.createElement('label');
        skip.className = 'hk-szett__option hk-szett__option--skip';
        skip.innerHTML = `<input class="hk-szett__input" type="radio" name="${name}" value=""><span class="hk-szett__option-title">Ez már megvan</span>`;
        container.append(skip);
      }
    }

    onGearChange(event) {
      const target = event.target;
      if (target.matches('select.hk-szett__variant')) {
        // Változatváltás: az ár és a link követi, és az opció kiválasztódik
        const option = target.closest('.hk-szett__option');
        const input = option.querySelector('input');
        const v = this.data.variants[target.value];
        input.value = target.value;
        option.querySelector('.hk-szett__price').textContent = formatPrice(v.pr);
        option.querySelector('.hk-szett__details').href = `${this.data.products[v.p].u}?variant=${target.value}`;
        input.checked = true;
      }
      for (const slot of this.gear.querySelectorAll('[data-slot]')) {
        for (const option of slot.querySelectorAll('.hk-szett__option')) {
          option.classList.toggle('is-selected', option.querySelector('input').checked);
        }
      }
      this.updateSummary();
    }

    selectedVariantIds() {
      return [...this.gear.querySelectorAll('.hk-szett__option input:checked')].map((i) => i.value).filter(Boolean);
    }

    updateSummary() {
      const ids = this.selectedVariantIds();
      const total = ids.reduce((sum, id) => sum + (this.data.variants[id]?.pr ?? 0), 0);
      this.querySelector('[data-summary-count]').textContent = ids.length ? `${ids.length} termék` : 'Nincs kiválasztott termék';
      this.querySelector('[data-summary-total]').textContent = ids.length ? `Összesen: ${formatPrice(total)}` : '';
      this.addButton.disabled = ids.length === 0;
      this.error.hidden = true;
    }

    updateUrl() {
      const url = new URL(window.location.href);
      if (this.fish) url.searchParams.set('hal', this.fish);
      else url.searchParams.delete('hal');
      if (this.size) url.searchParams.set('meret', this.size);
      else url.searchParams.delete('meret');
      window.history.replaceState(null, '', url);
    }

    async addToCart() {
      const ids = this.selectedVariantIds();
      if (!ids.length) return;
      this.addButton.disabled = true;
      this.addButton.setAttribute('aria-busy', 'true');
      this.error.hidden = true;
      try {
        const response = await fetch(`${this.dataset.cartAddUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: ids.map((id) => ({ id: Number(id), quantity: 1 })) }),
        });
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.description || body.message || 'A termékeket nem sikerült a kosárba tenni.');
        }
        window.location.href = this.dataset.cartUrl;
      } catch (error) {
        this.error.textContent = `${error.message} Próbálj másik változatot, vagy hagyd ki az érintett terméket.`;
        this.error.hidden = false;
        this.addButton.disabled = false;
      } finally {
        this.addButton.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('hk-szettvalaszto', HkSzettvalaszto);
}
